/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, useRef, type ReactNode, type CSSProperties } from "react";
import { SerpDeskLogo } from "./SerpDeskLogo";

/* ─── Color Palette ─── */
export const C = {
  hero: "#0b0d14", heroBorder: "rgba(255,255,255,.06)", heroMuted: "rgba(255,255,255,.38)",
  surface: "#fff", ghost: "#f7f8fb", warm: "#fafbfd",
  ink: "#0e1219", body: "#555d6e", muted: "#8c94a6", border: "#e5e9f0",
  purple: "#7c5cfc", purpleSoft: "#ede9fe", pink: "#f24e80",
  green: "#22c55e", teal: "#06b6d4", amber: "#f59e0b", red: "#ef4444", indigo: "#6366f1", blue: "#3b82f6",
} as const;

export const grad = `linear-gradient(135deg, ${C.purple}, ${C.pink})`;

/* ─── Single CTA URL (concierge demo) ─── */
export const CAL = "https://calendly.com/andrew-suver";

/* ─── The app (Launch fixes 1, 2026-10-19) ───
 * Launch fixes 3 (the PM's decision 5, 2026-10-26): the site has no signup switch of its own. "Start free trial" always
 * links to the app's signup page, which follows the app's one switch (seo-platform src/services/access/signupGate.ts
 * SIGNUP_OPEN): "SerpDesk is opening soon" with the demo link while it is closed, the form once it is open. Nothing on
 * this site changes when signup opens. */
export const APP_SIGNIN = "https://app.serpdesk.ai/api/signin";
export const APP_SIGNUP = "https://app.serpdesk.ai/api/signup";
export const SUPPORT_EMAIL = "support@serpdesk.ai";
export const MAILING_ADDRESS = "SerpDesk, Andrew Suver Ventures, LLC, 13300 Bothell Everett Hwy, Ste 303 PMB 704, Mill Creek, WA 98012";

/* ─── "Start free trial": always a link to the app's signup page (Launch fixes 3) ─── */
export function TrialCta({ variant = "nav", style, className }: { variant?: "nav" | "card" | "mobile"; style?: CSSProperties; className?: string }) {
  const base: CSSProperties = variant === "card"
    ? { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, textAlign: "center", fontSize: 14, fontWeight: 700, padding: "12px 20px", borderRadius: 10, textDecoration: "none" }
    : variant === "mobile"
      ? { display: "block", textAlign: "center", fontSize: 14, fontWeight: 600, padding: "11px 22px", borderRadius: 10, textDecoration: "none", marginTop: 12 }
      : { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, padding: "8px 16px", borderRadius: 10, textDecoration: "none", whiteSpace: "nowrap" };
  return <a href={APP_SIGNUP} className={className} style={{ ...base, color: "#fff", border: `1px solid rgba(255,255,255,.25)`, ...(variant === "card" ? { background: grad, border: "none" } : {}), ...style }}>Start free trial</a>;
}

/* ─── Intersection Observer Hook ─── */
export const useVis = (th = 0.1): [React.RefObject<HTMLDivElement | null>, boolean] => {
  const r = useRef<HTMLDivElement | null>(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const e = r.current;
    if (!e) return;
    const o = new IntersectionObserver(([x]) => {
      if (x.isIntersecting) { setV(true); o.disconnect(); }
    }, { threshold: th });
    o.observe(e);
    return () => o.disconnect();
  }, [th]);
  return [r, v];
};

/* ─── Reveal on Scroll ─── */
export const R = ({ children, d = 0, y = 24, s }: { children: ReactNode; d?: number; y?: number; s?: CSSProperties }) => {
  const [r, v] = useVis();
  return <div ref={r} style={{ opacity: v ? 1 : 0, transform: v ? "none" : `translateY(${y}px)`, transition: `all .7s cubic-bezier(.19,1,.22,1) ${d}s`, ...s }}>{children}</div>;
};

/* ─── Gradient Text ─── */
export const GT = ({ children }: { children: ReactNode }) => (
  <span style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{children}</span>
);

/* ─── Section Wrapper ─── */
export const Sec = ({ children, style, id }: { children: ReactNode; style?: CSSProperties; id?: string }) => (
  <section id={id} className="sec-wrap" style={{ padding: "96px 0", ...style }}>
    <div className="container-1160" style={{ maxWidth: 1160, margin: "0 auto", padding: "0 32px" }}>{children}</div>
  </section>
);

/* ─── Section Heading ─── */
export const H2c = ({ children }: { children: ReactNode }) => (
  <h2 className="h2c-mobile" style={{ fontFamily: "var(--display)", fontSize: 42, fontWeight: 800, textAlign: "center", letterSpacing: "-.03em", marginBottom: 14, lineHeight: 1.12 }}>{children}</h2>
);

/* ─── Subtitle ─── */
export const Sub = ({ children }: { children: ReactNode }) => (
  <p style={{ textAlign: "center", fontSize: 15.5, color: C.body, maxWidth: 620, margin: "0 auto 48px", lineHeight: 1.65 }}>{children}</p>
);

/* ─── Mock Browser Shell ─── */
export const MockShell = ({ children, title = "app.serpdesk.com", dark }: { children: ReactNode; title?: string; dark?: boolean }) => (
  <div style={{ background: dark ? `linear-gradient(135deg, ${C.hero}, #181c28)` : C.ghost, borderRadius: 16, border: `1px solid ${dark ? C.heroBorder : C.border}`, overflow: "hidden", boxShadow: dark ? "0 20px 50px rgba(0,0,0,.25)" : "0 12px 36px rgba(0,0,0,.06)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderBottom: `1px solid ${dark ? C.heroBorder : C.border}`, background: dark ? "rgba(255,255,255,.02)" : C.surface }}>
      {[0, 1, 2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: dark ? "rgba(255,255,255,.08)" : "#e5e7eb" }} />)}
      <div style={{ flex: 1, textAlign: "center", fontFamily: "var(--mono)", fontSize: 9, color: dark ? "rgba(255,255,255,.2)" : C.muted }}>{title}</div>
    </div>
    <div style={{ padding: 20 }}>{children}</div>
  </div>
);

/* ─── Feature Text (used by Feature) ─── */
export const FT = ({ headline, desc, bullets, dark, lc, link }: {
  headline: ReactNode; desc: string; bullets?: string[]; dark?: boolean; lc?: string; link?: string;
}) => (
  <div>
    <h3 style={{ fontFamily: "var(--display)", fontSize: 32, fontWeight: 800, lineHeight: 1.12, letterSpacing: "-.02em", marginBottom: 16, color: dark ? "#fff" : C.ink }}>{headline}</h3>
    <p style={{ fontSize: 15, color: dark ? "rgba(255,255,255,.55)" : C.body, lineHeight: 1.7, marginBottom: 20 }}>{desc}</p>
    {bullets && <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
      {bullets.map((b, i) => <div key={i} style={{ display: "flex", gap: 10, fontSize: 13.5, color: dark ? "rgba(255,255,255,.55)" : C.body, lineHeight: 1.5 }}><span style={{ color: lc || C.purple, flexShrink: 0, marginTop: 1 }}>✓</span>{b}</div>)}
    </div>}
    {link && <a href={link} style={{ fontSize: 13, color: lc, textDecoration: "none", fontWeight: 600 }}>Learn more →</a>}
  </div>
);

/* ─── Feature Section (two-column layout) ─── */
export const Feature = ({ headline, desc, bullets, link, linkColor, visual, reverse, dark }: {
  headline: ReactNode; desc: string; bullets?: string[]; link?: string; linkColor?: string;
  visual: ReactNode; reverse?: boolean; dark?: boolean;
}) => (
  <div className={`feature-grid${reverse ? " reverse-feature" : ""}`} style={{ display: "grid", gridTemplateColumns: reverse ? "1.1fr 1fr" : "1fr 1.1fr", gap: 48, alignItems: "center" }}>
    {reverse
      ? <>{visual}<FT headline={headline} desc={desc} bullets={bullets} dark={dark} lc={linkColor} link={link} /></>
      : <><FT headline={headline} desc={desc} bullets={bullets} dark={dark} lc={linkColor} link={link} />{visual}</>}
  </div>
);

/* ─── Tool-name pill row (used by sub-pages) ─── */
export const ToolPills = ({ pills, dark }: { pills: string[]; dark?: boolean }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 22 }}>
    {pills.map(p => (
      <span key={p} style={{
        fontFamily: "var(--mono)",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: ".08em",
        padding: "6px 12px",
        borderRadius: 999,
        border: `1px solid ${dark ? "rgba(255,255,255,.12)" : C.border}`,
        color: dark ? "rgba(255,255,255,.65)" : C.muted,
        background: dark ? "rgba(255,255,255,.03)" : C.surface,
        textTransform: "uppercase",
      }}>{p}</span>
    ))}
  </div>
);

/* ─── Navigation (scroll-aware) ───
 * FE1 (2026-10-28, the PM's choice on screen): the Platform menu and the Competitors link went with the seven product
 * pages they opened (they described the May platform; App.tsx sends them to Home). */
export function Nav() {
  const [scrollY, setScrollY] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const navLink: CSSProperties = { fontSize: 13, color: "rgba(255,255,255,.55)", textDecoration: "none", fontWeight: 500, whiteSpace: "nowrap" };

  return (
    <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, background: scrollY > 60 ? "rgba(11,13,20,.95)" : "transparent", backdropFilter: scrollY > 60 ? "blur(20px)" : "none", borderBottom: scrollY > 60 ? `1px solid ${C.heroBorder}` : "none", transition: "all .35s" }}>
      <div style={{ maxWidth: 1160, margin: "0 auto", padding: "13px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <a href="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} aria-label="SerpDesk home">
          <SerpDeskLogo height={42} />
        </a>

        {/* Desktop nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 28 }} className="nav-desktop">
          <a href="/case-studies" style={navLink}>Case Studies</a>
          <a href="/pricing" style={navLink}>Pricing</a>
          <a href={APP_SIGNIN} style={navLink}>Sign in</a>
          <TrialCta variant="nav" className="nav-trial" />
          <a href={CAL} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 700, color: "#fff", padding: "9px 22px", borderRadius: 10, background: grad, textDecoration: "none", whiteSpace: "nowrap" }}>Book a demo</a>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: 4, color: "#fff", fontSize: 22 }}
          className="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? "✕" : "☰"}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{ background: "rgba(11,13,20,.97)", borderTop: `1px solid ${C.heroBorder}`, padding: "16px 32px 24px" }} className="nav-mobile-menu">
          <a href="/case-studies" style={{ display: "block", fontSize: 14, color: "rgba(255,255,255,.7)", textDecoration: "none", padding: "10px 0", borderBottom: `1px solid ${C.heroBorder}` }}>Case Studies</a>
          <a href="/pricing" style={{ display: "block", fontSize: 14, color: "rgba(255,255,255,.7)", textDecoration: "none", padding: "10px 0", borderBottom: `1px solid ${C.heroBorder}` }}>Pricing</a>
          <a href={APP_SIGNIN} style={{ display: "block", fontSize: 14, color: "rgba(255,255,255,.7)", textDecoration: "none", padding: "10px 0" }}>Sign in</a>
          <TrialCta variant="mobile" />
          <a href={CAL} target="_blank" rel="noopener noreferrer" style={{ display: "block", fontSize: 14, fontWeight: 700, color: "#fff", padding: "12px 22px", borderRadius: 10, background: grad, textDecoration: "none", textAlign: "center", marginTop: 14 }}>Book a demo</a>
        </div>
      )}
    </nav>
  );
}

/* ─── Footer ─── */
export function Footer() {
  return (
    <footer style={{ background: C.hero, padding: "48px 0 32px", color: "#fff" }}>
      <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 32px" }}>
        <div style={{ height: 2, background: grad, borderRadius: 2, marginBottom: 36, opacity: .3 }} />
        <div style={{ display: "grid", gridTemplateColumns: "1.8fr 1fr 1fr", gap: 36, marginBottom: 36 }} className="footer-grid">
          <div>
            <div style={{ marginBottom: 12 }}>
              <SerpDeskLogo height={38} />
            </div>
            <p style={{ fontSize: 12.5, color: C.heroMuted, lineHeight: 1.6, maxWidth: 240 }}>SEO changes you approve. Nothing is published until you approve it.</p>
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.5)", marginBottom: 8, letterSpacing: ".04em" }}>Contact</div>
              <a href={`mailto:${SUPPORT_EMAIL}`} style={{ display: "block", fontSize: 12.5, color: C.heroMuted, textDecoration: "none", padding: "2px 0" }}>{SUPPORT_EMAIL}</a>
              <address style={{ fontStyle: "normal", fontSize: 12, color: C.heroMuted, lineHeight: 1.6, maxWidth: 260, marginTop: 4 }}>{MAILING_ADDRESS}</address>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.5)", marginBottom: 14, letterSpacing: ".04em" }}>Company</div>
            <a href="/case-studies" style={{ display: "block", fontSize: 12.5, color: C.heroMuted, textDecoration: "none", padding: "3px 0" }}>Case Studies</a>
            <a href="/pricing" style={{ display: "block", fontSize: 12.5, color: C.heroMuted, textDecoration: "none", padding: "3px 0" }}>Pricing</a>
            <a href={APP_SIGNIN} style={{ display: "block", fontSize: 12.5, color: C.heroMuted, textDecoration: "none", padding: "3px 0" }}>Sign in</a>
            <a href={CAL} target="_blank" rel="noopener noreferrer" style={{ display: "block", fontSize: 12.5, color: C.heroMuted, textDecoration: "none", padding: "3px 0" }}>Book a demo</a>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.5)", marginBottom: 14, letterSpacing: ".04em" }}>Legal</div>
            <a href="/privacy" style={{ display: "block", fontSize: 12.5, color: C.heroMuted, textDecoration: "none", padding: "3px 0" }}>Privacy</a>
            <a href="/terms" style={{ display: "block", fontSize: 12.5, color: C.heroMuted, textDecoration: "none", padding: "3px 0" }}>Terms</a>
            <a href="/refund" style={{ display: "block", fontSize: 12.5, color: C.heroMuted, textDecoration: "none", padding: "3px 0" }}>Refunds and cancellation</a>
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${C.heroBorder}`, paddingTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
          <p style={{ fontSize: 10.5, color: "rgba(255,255,255,.32)", lineHeight: 1.55, maxWidth: 920 }}>
            Examples on this site are samples unless marked as client results. No SEO outcome is guaranteed: results depend on your market, links, site authority and competition.
          </p>
          <span style={{ fontSize: 11, color: "rgba(255,255,255,.3)" }}>© 2026 SerpDesk · Built by Andrew Suver</span>
        </div>
      </div>
    </footer>
  );
}
